import React, { useState, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { adminPostService } from '../../../services/adminPostService';
import { Loader2, ArrowLeft, Upload } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import JoditEditor from 'jodit-react';
import PostCommentSection from '../../../components/admin/PostCommentSection';

const EditPost = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        is_published: false,
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [errors, setErrors] = useState({});

    // Simple slug preview
    const slugPreview = formData.title.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');

    // Jodit Editor Configuration
    const editorConfig = useMemo(() => {
        // Get CSRF token from meta tag
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        return {
            readonly: false,
            height: 500,
            uploader: {
                url: '/api/admin/upload-image',
                insertImageAsBase64URI: false,
                imagesExtensions: ['jpg', 'png', 'jpeg', 'gif', 'webp'],
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token || ''
                },
                filesVariableName: () => 'file',
                isSuccess: (resp) => resp.success,
                getMessage: (resp) => resp.message,
                process: (resp) => {
                    // Return the uploaded file info
                    return {
                        files: [resp.url],
                        path: resp.url,
                        baseurl: window.location.origin,
                        error: resp.success ? '' : (resp.message || 'Upload failed'),
                        msg: resp.message || 'Image uploaded successfully'
                    };
                },
                defaultHandlerSuccess: function (data, resp) {
                    const files = data.files || [];
                    if (files.length) {
                        this.selection.insertImage(files[0]);
                    }
                }
            },
            buttons: [
                'bold', 'italic', 'underline', '|',
                'ul', 'ol', '|',
                'font', 'fontsize', '|',
                'align', '|',
                'link', 'image', '|',
                'table', '|',
                'undo', 'redo', '|',
                'hr', 'source'
            ],
            colors: {
                greyscale: ['#000000', '#31415A', '#5C5935', '#888888', '#FFFFFF'],
                palette: ['#C9A14F', '#F7F1CD', '#31415A', '#5C5935']
            },
            toolbarAdaptive: false,
            showCharsCounter: true,
            showWordsCounter: true,
            showXPathInStatusbar: false,
            placeholder: 'Start writing your blog content here... You can add images, format text, and more!'
        };
    }, []);

    useEffect(() => {
        fetchPost();
    }, [id]);

    const fetchPost = async () => {
        try {
            const response = await adminPostService.getPost(id);
            const post = response.data.post;
            setFormData({
                title: post.title,
                content: post.content,
                is_published: post.is_published,
                image: null // New image file, kept null unless changed
            });
            if (post.image) {
                // Determine full URL if needed (api returns relative usually, but controller does /storage)
                // Assuming REACT_APP_API_URL or base handling elsewhere. 
                // Since Create uses objectURL, here we just use the string.
                // If using localhost, might need to prepend backend URL.
                // Controller returns '/storage/...', so just use that directly if on same domain or relative
                setImagePreview(post.image);
            }
        } catch (error) {
            console.error("Failed to fetch post", error);
            toast.error("Failed to load post");
            navigate('/admin/posts');
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, image: file });
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});

        const data = new FormData();
        data.append('title', formData.title);
        data.append('content', formData.content);
        data.append('is_published', formData.is_published ? '1' : '0');
        if (formData.image) {
            data.append('image', formData.image);
        }

        try {
            await adminPostService.updatePost(id, data);
            toast.success("Post updated successfully");
            navigate('/admin/posts');
        } catch (error) {
            console.error(error);
            if (error.response && error.response.status === 422) {
                setErrors(error.response.data.errors);
            } else {
                toast.error("Failed to update post");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 font-sans text-[#31415A] min-w-0">
            <Toaster position="top-right" />

            <form onSubmit={handleSubmit} className="max-w-6xl mx-auto h-auto md:h-[calc(100vh-140px)] flex flex-col">
                {/* Header Actions */}
                <div className="mb-4 md:mb-6 flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100 gap-4">
                    <div className="flex items-center gap-4 w-full md:w-auto">
                        <Link to="/admin/posts" className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                        <div>
                            <h1 className="text-xl font-bold text-[#31415A]">Edit Post</h1>
                            <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                                <span>Status:</span>
                                <span className={`font-bold uppercase tracking-wider ${formData.is_published ? 'text-green-600' : 'text-amber-600'}`}>
                                    {formData.is_published ? 'Published' : 'Draft'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                        <div className="flex items-center gap-2 w-full sm:w-auto bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 justify-center">
                            <input
                                type="checkbox"
                                id="is_published"
                                checked={formData.is_published}
                                onChange={e => setFormData({ ...formData, is_published: e.target.checked })}
                                className="w-4 h-4 text-[#31415A] rounded border-gray-300 focus:ring-[#31415A] cursor-pointer"
                            />
                            <label htmlFor="is_published" className="text-sm font-medium text-gray-700 select-none cursor-pointer">
                                Publish Immediately
                            </label>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full sm:w-auto bg-[#31415A] text-[#F7F1CD] px-6 py-2.5 rounded-lg font-bold hover:bg-[#253246] transition-all disabled:opacity-50 flex justify-center items-center gap-2 shadow-lg shadow-[#31415A]/20"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" /> Updating...
                                </>
                            ) : (
                                'Update Post'
                            )}
                        </button>
                    </div>
                </div>

                {/* Editor Content */}
                <div className="flex-1 flex flex-col md:flex-row gap-6 md:overflow-hidden min-h-0">
                    <div className="w-full md:w-1/2 flex flex-col gap-6 md:overflow-y-auto pr-0 md:pr-2 pb-8">
                        {/* Title & Slug */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Article Title</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={e => setFormData({ ...formData, title: e.target.value })}
                                className="w-full text-2xl font-bold text-gray-800 placeholder-gray-300 border-b border-gray-200 px-0 py-2 focus:border-[#31415A] focus:ring-0 outline-none transition-all"
                                placeholder="Enter title here..."
                            />
                            {errors.title && <div className="text-red-500 text-xs mt-1">{errors.title[0]}</div>}
                            <div className="mt-2 text-xs text-gray-400 font-mono">
                                slug: /{slugPreview}
                            </div>
                        </div>

                        {/* Cover Image Upload */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Cover Image</label>
                            <div className="flex gap-4 items-start">
                                <div className="flex-1">
                                    <div className="relative">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-[#31415A]/10 file:text-[#31415A] hover:file:bg-[#31415A]/20 transition-all cursor-pointer"
                                        />
                                    </div>
                                    {errors.image && <div className="text-red-500 text-xs mt-1">{errors.image[0]}</div>}
                                    <p className="text-xs text-gray-400 mt-2">Recommended: 16:9 aspect ratio, max 2MB.</p>
                                </div>
                                <div className="w-24 h-16 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shrink-0">
                                    {imagePreview ? (
                                        <img src={imagePreview} className="w-full h-full object-cover" alt="Preview" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                                            <Upload className="w-6 h-6" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Content Editor (Rich Text) */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-h-[550px]">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-4 pt-4">Content</label>
                            <div className="px-4 pb-4">
                                <JoditEditor
                                    value={formData.content}
                                    config={editorConfig}
                                    onBlur={newContent => setFormData({ ...formData, content: newContent })}
                                    onChange={newContent => { }}
                                />
                            </div>
                            {errors.content && <div className="text-red-500 text-xs mt-1 px-4 pb-4">{errors.content[0]}</div>}
                        </div>
                    </div>

                    {/* Live Preview Column (Right) */}
                    <div className="w-1/2 bg-[#31415A] rounded-xl overflow-hidden shadow-2xl border border-[#31415A]/20 flex flex-col hidden md:flex">
                        <div className="bg-[#253246] px-4 py-2 flex justify-between items-center border-b border-[#F7F1CD]/5 shrink-0">
                            <span className="text-[#F7F1CD]/50 text-xs font-bold uppercase tracking-widest">Live Preview</span>
                            <div className="flex gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-red-400 opacity-50"></div>
                                <div className="w-2.5 h-2.5 rounded-full bg-amber-400 opacity-50"></div>
                                <div className="w-2.5 h-2.5 rounded-full bg-green-400 opacity-50"></div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto bg-[#31415A] text-[#F7F1CD] relative font-['Montserrat'] scrollbar-hide">
                            {/* Hero Section */}
                            <div className="relative h-[250px] w-full shrink-0">
                                {imagePreview ? (
                                    <img src={imagePreview} className="w-full h-full object-cover" alt="Cover" />
                                ) : (
                                    <div className="w-full h-full bg-[#1F2937] flex items-center justify-center text-gray-600">
                                        <span className="text-xs uppercase tracking-widest">No Cover Image</span>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-[#31415A] via-[#31415A]/60 to-transparent"></div>
                                <div className="absolute inset-0 flex flex-col justify-end items-center text-center pb-8 px-4">
                                    <span className="bg-amber-500 text-[#31415A] px-3 py-0.5 rounded-full text-[0.6rem] font-bold uppercase tracking-wider mb-3 block w-fit mx-auto">Article</span>
                                    <h1 className="text-2xl font-bold mb-3 leading-tight">{formData.title || 'Untitled Post'}</h1>
                                    <div className="flex items-center gap-3 justify-center text-[0.65rem] font-medium opacity-80">
                                        <span>{new Date().toLocaleDateString()}</span>
                                        <span className="w-0.5 h-0.5 bg-amber-500 rounded-full"></span>
                                        <span>By Admin</span>
                                    </div>
                                </div>
                            </div>

                            {/* Content Body */}
                            <div className="p-8">
                                <article className="prose prose-invert prose-sm max-w-none text-[#F7F1CD]/90 leading-relaxed whitespace-pre-wrap">
                                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.content || '<p class="opacity-30 italic">Content will appear here...</p>') }} />
                                </article>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            {/* Comments Section */}
            <div className="max-w-6xl mx-auto mt-8">
                <PostCommentSection postId={id} />
            </div>
        </div>
    );
};

export default EditPost;
